import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/useAuth';
import { NEW_DESIGN_PROJECTS_PATH, projectsPathForDesign, readDesign } from '@/lib/designChoice';
import { ToastProvider } from '@/contexts/ToastContext';
import { TeleportProvider } from '@/contexts/TeleportContext';
import { ChatAttachDragProvider } from '@/contexts/ChatAttachDragContext';
import { Navbar } from '@/components/Layout/Navbar';
import { AccessEmptyState } from '@/components/Layout/AccessEmptyState';
import { AccessPreviewBanner } from '@/components/Layout/AccessPreviewBanner';
import { isModulePathAllowed, hasTrackerNavAccess, resolveDefaultHomePath } from '@/types/access';
import { ProjectsTableFiltersProvider } from '@/contexts/ProjectsTableFiltersContext';
import {
  CatalogManageOpenerProvider,
  type OpenCatalogManageOptions,
} from '@/contexts/CatalogManageOpenerContext';
import { ProjectsSearchProvider } from '@/contexts/ProjectsSearchContext';
import { ScheduleSearchProvider } from '@/contexts/ScheduleSearchContext';
import { ProjectsColumnSearchProvider } from '@/contexts/ProjectsColumnSearchContext';
import { BrowserNotificationPrompt } from '@/components/Notifications/BrowserNotificationPrompt';
import { AddProjectModal } from '@/components/AddProjectModal/AddProjectModal';
import { CatalogManageModal } from '@/components/CatalogManageModal/CatalogManageModal';
import { AuthSessionBridge } from '@/components/Auth/AuthSessionBridge';
import { AccessPolicySync } from '@/components/Auth/AccessPolicySync';
import { RealTimeSync } from '@/components/RealTimeSync/RealTimeSync';
import { AppUpdate } from '@/components/AppUpdate/AppUpdate';
import { NotificationSync } from '@/components/Notifications/NotificationSync';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { SchedulePage } from '@/pages/SchedulePage';
import { AuthPage } from '@/pages/AuthPage';

/** Раздел нового дизайна — отдельный чанк: его библиотеки и стили не попадают в основной бандл. */
const NewDesignRoutes = lazy(() =>
  import('@/features/newdesign/NewDesignRoutes').then((m) => ({ default: m.NewDesignRoutes }))
);
import { ReadersPage } from '@/pages/ReadersPage';
import { StudiosPage } from '@/pages/StudiosPage';
import { FonotekaPage } from '@/pages/FonotekaPage';
import { MiniAppFloatingPanel } from '@/components/MiniApps/MiniAppFloatingPanel';
import { ReadersCatalogFiltersProvider } from '@/contexts/ReadersCatalogFiltersContext';
import { ReadersCatalogSearchProvider } from '@/contexts/ReadersCatalogSearchContext';
import { StudiosCatalogSearchProvider } from '@/contexts/StudiosCatalogSearchContext';
import { GlobalAudioProvider } from '@/audio/GlobalAudioContext';
import { AudioBar } from '@/features/newdesign/audio/AudioBar';
import '@/styles/index.css';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  },
});

queryClient.setQueryDefaults(['project'], {
  staleTime: Infinity,
  gcTime: Infinity,
});

const today = new Date();

function ProtectedLayout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="app-loading">Загрузка…</div>;
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  return (
    <>
      {children}
      {/* Только за входом: проверка версии слушает сокет, а он есть у вошедшего. Экран входа и так открывается свежей загрузкой. */}
      <AppUpdate />
    </>
  );
}

/**
 * Адрес «/» открывает выбранный дизайн (lib/designChoice), по умолчанию — новый. Обёртка, а не отдельный маршрут «/»:
 * иначе переход из «/» в «/projects» пересоздавал бы всю старую оболочку.
 */
function DesignHome({ children }: { children: React.ReactNode }) {
  const { capabilities } = useAuth();
  const { pathname } = useLocation();
  if (pathname === '/' && readDesign() === 'new' && capabilities.projects.read) {
    return <Navigate to={NEW_DESIGN_PROJECTS_PATH} replace />;
  }
  return <>{children}</>;
}

function isProjectsModulePath(pathname: string): boolean {
  return pathname === '/' || pathname === '/projects' || pathname.startsWith('/projects/');
}

function ModuleRouteGuard({ children }: { children: React.ReactNode }) {
  const { capabilities } = useAuth();
  const location = useLocation();
  const home = resolveDefaultHomePath(capabilities, projectsPathForDesign());
  if (!isModulePathAllowed(location.pathname, capabilities)) {
    const homeAllowed = isModulePathAllowed(home, capabilities);
    const sameAsHome =
      location.pathname === home ||
      (isProjectsModulePath(location.pathname) && isProjectsModulePath(home));
    if (homeAllowed && !sameAsHome) {
      return <Navigate to={home} replace />;
    }
    return <AccessEmptyState />;
  }
  return <>{children}</>;
}

function TrackerShell() {
  const { capabilities } = useAuth();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [miniAppPanelOpen, setMiniAppPanelOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogOpts, setCatalogOpts] = useState<OpenCatalogManageOptions>({});
  const catalogPersonCreatedRef = useRef<OpenCatalogManageOptions['onPersonCreated']>(undefined);
  const catalogStudioCreatedRef = useRef<OpenCatalogManageOptions['onStudioCreated']>(undefined);
  const catalogLegalEntityCreatedRef = useRef<OpenCatalogManageOptions['onLegalEntityCreated']>(undefined);
  const [scheduleYear, setScheduleYear] = useState(today.getFullYear());
  const [scheduleMonth, setScheduleMonth] = useState(today.getMonth());

  useEffect(() => {
    if (!capabilities.canCreateProject) setAddModalOpen(false);
    if (!capabilities.hasAnyGrant) setMiniAppPanelOpen(false);
    if (!capabilities.persons.write) setCatalogOpen(false);
  }, [capabilities]);

  const handleAddProject = useCallback(() => {
    setAddModalOpen(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setAddModalOpen(false);
  }, []);

  const handleOpenMiniAppPanel = useCallback(() => {
    setMiniAppPanelOpen(true);
  }, []);

  const handleCloseMiniAppPanel = useCallback(() => {
    setMiniAppPanelOpen(false);
  }, []);

  const openCatalogManageInner = useCallback((opts: OpenCatalogManageOptions = {}) => {
    catalogPersonCreatedRef.current = opts.onPersonCreated;
    catalogStudioCreatedRef.current = opts.onStudioCreated;
    catalogLegalEntityCreatedRef.current = opts.onLegalEntityCreated;
    setCatalogOpts(opts);
    setCatalogOpen(true);
  }, []);

  const openCatalogManage = useCallback(
    (opts: OpenCatalogManageOptions = {}) => {
      if (!capabilities.persons.write) return;
      openCatalogManageInner(opts);
    },
    [capabilities.persons.write, openCatalogManageInner]
  );

  const handleCloseCatalogManage = useCallback(() => {
    setCatalogOpen(false);
    catalogPersonCreatedRef.current = undefined;
    catalogStudioCreatedRef.current = undefined;
    catalogLegalEntityCreatedRef.current = undefined;
    setCatalogOpts({});
  }, []);

  const handleCatalogPersonCreated = useCallback(
    (payload: Parameters<NonNullable<OpenCatalogManageOptions['onPersonCreated']>>[0]) => {
      catalogPersonCreatedRef.current?.(payload);
    },
    []
  );

  const handleCatalogStudioCreated = useCallback(
    (studio: Parameters<NonNullable<OpenCatalogManageOptions['onStudioCreated']>>[0]) => {
      catalogStudioCreatedRef.current?.(studio);
    },
    []
  );

  const handleCatalogLegalEntityCreated = useCallback(
    (entity: Parameters<NonNullable<OpenCatalogManageOptions['onLegalEntityCreated']>>[0]) => {
      catalogLegalEntityCreatedRef.current?.(entity);
    },
    []
  );

  const handleScheduleMonthChange = useCallback((year: number, month: number) => {
    setScheduleYear(year);
    setScheduleMonth(month);
  }, []);

  return (
    <TeleportProvider onScheduleMonthChange={handleScheduleMonthChange}>
      <ChatAttachDragProvider>
        <ProjectsSearchProvider>
          <ScheduleSearchProvider>
            <ProjectsColumnSearchProvider>
              <ProjectsTableFiltersProvider>
                <ReadersCatalogFiltersProvider>
                  <ReadersCatalogSearchProvider>
                    <StudiosCatalogSearchProvider>
                      <CatalogManageOpenerProvider
                          openCatalogManage={openCatalogManage}
                          canManageCatalog={capabilities.persons.write}
                        >
                          <div className="app">
                            <Navbar
                              onAddProject={capabilities.canCreateProject ? handleAddProject : undefined}
                              onAddPerson={
                                capabilities.persons.write
                                  ? () => openCatalogManage({ tab: 'persons' })
                                  : undefined
                              }
                              onOpenMiniAppPanel={
                                capabilities.hasAnyGrant ? handleOpenMiniAppPanel : undefined
                              }
                              scheduleYear={scheduleYear}
                              scheduleMonth={scheduleMonth}
                              onScheduleMonthChange={handleScheduleMonthChange}
                            />
                            <AccessPreviewBanner />
                            <main className="app__main">
                              {!capabilities.hasAnyGrant || !hasTrackerNavAccess(capabilities) ? (
                                <AccessEmptyState />
                              ) : (
                                <div className="app__routes">
                                  <ModuleRouteGuard>
                                    <Routes>
                                      <Route path="/" element={<ProjectsPage />} />
                                      <Route path="/projects" element={<ProjectsPage />} />
                                      <Route
                                        path="/readers"
                                        element={
                                          <ReadersPage
                                            onOpenPersonManage={
                                              capabilities.persons.write
                                                ? (personId) =>
                                                    openCatalogManage({ tab: 'persons', personId })
                                                : undefined
                                            }
                                          />
                                        }
                                      />
                                      <Route path="/studios" element={<StudiosPage />} />
                                      <Route path="/fonoteka" element={<FonotekaPage />} />
                                      <Route
                                        path="/schedule"
                                        element={
                                          <div className="app__route-schedule">
                                            <SchedulePage year={scheduleYear} month={scheduleMonth} />
                                          </div>
                                        }
                                      />
                                    </Routes>
                                  </ModuleRouteGuard>
                                </div>
                              )}
                            </main>
                            <AddProjectModal open={addModalOpen} onClose={handleCloseAddModal} />
                            <MiniAppFloatingPanel open={miniAppPanelOpen} onClose={handleCloseMiniAppPanel} />
                            <CatalogManageModal
                              open={catalogOpen}
                              onClose={handleCloseCatalogManage}
                              initialTab={catalogOpts.tab ?? 'persons'}
                              personIdPreset={catalogOpts.personId ?? null}
                              studioIdPreset={catalogOpts.studioId ?? null}
                              legalEntityIdPreset={catalogOpts.legalEntityId ?? null}
                              rolePreset={catalogOpts.rolePreset ?? null}
                              editionIdPreset={catalogOpts.editionIdPreset ?? null}
                              initialNameSeed={catalogOpts.nameSeed ?? null}
                              onPersonCreated={handleCatalogPersonCreated}
                              onStudioCreated={handleCatalogStudioCreated}
                              onLegalEntityCreated={handleCatalogLegalEntityCreated}
                            />
                            <BrowserNotificationPrompt />
                          </div>
                        </CatalogManageOpenerProvider>
                    </StudiosCatalogSearchProvider>
                  </ReadersCatalogSearchProvider>
                </ReadersCatalogFiltersProvider>
              </ProjectsTableFiltersProvider>
            </ProjectsColumnSearchProvider>
          </ScheduleSearchProvider>
        </ProjectsSearchProvider>
      </ChatAttachDragProvider>
    </TeleportProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <ToastProvider>
      <AuthSessionBridge />
      <RealTimeSync />
      <AccessPolicySync />
      <NotificationSync />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        {/* Плеер общий для старого и нового дизайна: музыка не обрывается при переходе между ними */}
        <GlobalAudioProvider>
        <div className="app">
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route
              path="/new/*"
              element={
                <ProtectedLayout>
                  <ModuleRouteGuard>
                    <Suspense fallback={<div className="app-loading">Загрузка…</div>}>
                      <NewDesignRoutes />
                    </Suspense>
                  </ModuleRouteGuard>
                </ProtectedLayout>
              }
            />
            <Route
              path="/*"
              element={
                <ProtectedLayout>
                  <DesignHome>
                    <TrackerShell />
                  </DesignHome>
                </ProtectedLayout>
              }
            />
          </Routes>
        </div>
        <AudioBar />
        </GlobalAudioProvider>
      </BrowserRouter>
      </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
